/**
 * AlertBadge Component
 *
 * Color-coded priority indicator badge for alerts
 * TASK-164: Attention-Focused Dashboard UI
 */

import React from 'react';
import { AlertLevel, PRIORITY_COLORS } from '../../../types/dashboard';
import { AlertCircle, AlertTriangle, Info, XCircle } from 'lucide-react';

interface AlertBadgeProps {
  level: AlertLevel;
  showIcon?: boolean;
  showLabel?: boolean;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const AlertBadge: React.FC<AlertBadgeProps> = ({
  level,
  showIcon = true,
  showLabel = true,
  size = 'md',
  className = '',
}) => {
  const colors = PRIORITY_COLORS[level];

  // Size configurations
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-0.5',
    lg: 'text-base px-3 py-1',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // Get appropriate icon component
  const getIconComponent = () => {
    switch (level) {
      case 'critical':
        return <XCircle className={`${iconSizes[size]} ${colors.icon}`} />;
      case 'high':
        return <AlertCircle className={`${iconSizes[size]} ${colors.icon}`} />;
      case 'medium':
        return <AlertTriangle className={`${iconSizes[size]} ${colors.icon}`} />;
      case 'info':
        return <Info className={`${iconSizes[size]} ${colors.icon}`} />;
      default:
        return null;
    }
  };

  // Label text
  const getLevelLabel = () => {
    switch (level) {
      case 'critical':
        return 'Critical';
      case 'high':
        return 'High';
      case 'medium':
        return 'Medium';
      case 'info':
        return 'Info';
      default:
        return '';
    }
  };

  return (
    <span
      className={`
        inline-flex items-center gap-1 rounded-full font-semibold
        ${colors.badge} ${colors.text} ${colors.border} border
        ${sizeClasses[size]}
        ${className}
      `}
    >
      {showIcon && getIconComponent()}
      {showLabel && <span>{getLevelLabel()}</span>}
    </span>
  );
};

export default AlertBadge;
